import { Button } from '@pelckmans/business-components/components/button';
import { Dialog, ModalDialog } from '@pelckmans/business-components/components/dialog';
import { forwardRef, useContext, useRef } from 'react';
import { Node } from '../_api/TableOfContentsApi';
import { useTranslation } from 'react-i18next';
import MaterialModalHeader from './common/MaterialModalHeader';
import MedialinkContentType from '../_components/common/MedialinkContentType';
import ModalContext from '../../../contexts/ModalContext';
import { Medialink } from '../_api/MedialinksApi';

type WeblinkInfo = {
  width?: number;
  height?: number;
  reload?: boolean;
};

type Props = {
  id: string;
  medialink: Medialink;
  weblinkInfo: WeblinkInfo;
  node: Node;
  url: string;
};

const ReloadButton = forwardRef<HTMLIFrameElement, { url: string }>(function ReloadButton({ url }, ref) {
  const { t } = useTranslation();

  const reloadIframe = () => {
    if (ref && 'current' in ref && ref.current) ref.current.src = url;
  };

  return (
    <Button type="button" onClick={reloadIframe} variant="secondary">
      {t('ACTIONS.RELOAD')}
    </Button>
  );
});

export default function WeblinkModal({ id, medialink, weblinkInfo, node, url }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { closeModal } = useContext(ModalContext);

  const { t } = useTranslation();

  return (
    <ModalDialog
      id={id}
      closeTitle={t('ACTIONS.CLOSE')}
      closeViaEscape
      closeOnClickOutside
      onClose={() => closeModal(id)}
      className="material-dialog"
      fullScreen={!weblinkInfo.width && !weblinkInfo.height}
      width={weblinkInfo.width ? `${weblinkInfo.width}px` : undefined}
      height={weblinkInfo.height ? `${weblinkInfo.height}px` : undefined}
    >
      <MaterialModalHeader
        title={medialink.name}
        description={node.description}
        contentType={<MedialinkContentType medialink={medialink} />}
        endAdornment={weblinkInfo.reload ? <ReloadButton ref={iframeRef} url={url} /> : undefined}
      />
      <Dialog.Body data-testid="dialog-body">
        <iframe role="presentation" ref={iframeRef} src={url} title={medialink.name} allow="fullscreen" />
      </Dialog.Body>
    </ModalDialog>
  );
}
